import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { formatEventDate } from "@/lib/api";
import { formatSlots, isOpenNow, todayKey } from "@/lib/hours";

export const directionsUrl = (p) => {
  const label = [p.name || p.venue, p.address, p.city, p.country].filter(Boolean).join(", ");
  const dest = p.address || typeof p.lat !== "number" ? label : `${p.lat},${p.lng}`;
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`;
};

export const GoButton = ({ place, testId, className = "" }) => (
  <a href={directionsUrl(place)} target="_blank" rel="noreferrer" data-testid={testId}
    className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-white underline decoration-primary decoration-2 underline-offset-4 hover:text-primary transition-colors ${className}`}>
    Y aller →
  </a>
);

const eventIcon = L.divIcon({
  className: "",
  iconSize: [22, 22],
  iconAnchor: [11, 11],
  html: '<span style="display:block;width:22px;height:22px;border-radius:9999px;background:#0a0a0a;border:3px solid #f2490a;box-shadow:0 0 0 3px #f2490a44"></span>',
});

export const CATEGORY_STYLE = {
  disquaires: { label: "Disquaire", color: "#f2490a" },
  salles: { label: "Salle de concert", color: "#ffffff" },
  bars: { label: "Bar / Restaurant", color: "#f5c518" },
  clubs: { label: "Club", color: "#3fa9f5" },
};

const icon = (category, active) => {
  const c = CATEGORY_STYLE[category]?.color || "#ff4d00";
  const size = active ? 22 : 16;
  return L.divIcon({
    className: "",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    html: `<span style="display:block;width:${size}px;height:${size}px;background:${c};border:2px solid #0a0a0a;box-shadow:0 0 0 2px ${c}55"></span>`,
  });
};

// Places only known by their city share one point: spread them in a small circle so each stays clickable.
const spread = (places) => {
  const groups = {};
  places.forEach((p) => {
    const k = `${p.lat.toFixed(3)},${p.lng.toFixed(3)}`;
    (groups[k] = groups[k] || []).push(p);
  });
  return Object.values(groups).flatMap((g) =>
    g.map((p, i) => (g.length === 1 ? p : {
      ...p,
      lat: p.lat + 0.006 * Math.cos((2 * Math.PI * i) / g.length),
      lng: p.lng + 0.009 * Math.sin((2 * Math.PI * i) / g.length),
    })));
};

const userIcon = L.divIcon({
  className: "",
  iconSize: [18, 18],
  iconAnchor: [9, 9],
  html: '<span style="display:block;width:18px;height:18px;border-radius:9999px;background:#3b82f6;border:3px solid #fff;box-shadow:0 0 0 6px #3b82f655"></span>',
});

const FitBounds = ({ points, focus, userPos }) => {
  const map = useMap();
  useEffect(() => {
    if (focus) {
      map.flyTo([focus.lat, focus.lng], 14, { duration: 0.8 });
      return;
    }
    if (userPos) {
      map.flyTo([userPos.lat, userPos.lng], 11, { duration: 0.8 });
      return;
    }
    if (points.length) map.fitBounds(points.map((p) => [p.lat, p.lng]), { padding: [40, 40], maxZoom: 12 });
  }, [map, points, focus, userPos]);
  return null;
};

const PlaceConcerts = ({ events }) => (
  <div className="mt-3 pt-3 border-t border-white/15" data-testid="places-map-popup-concerts">
    <p className="lgtdz-popup-kicker">Prochains concerts ici</p>
    {events.slice(0, 3).map((e) => (
      <Link key={e.id} to={`/agenda/${e.slug}`} className="block mt-2 text-white hover:text-primary" data-testid={`places-map-popup-concert-${e.id}`}>
        <span className="block text-[12px] text-zinc-400">{formatEventDate(e.date)}</span>
        <span className="block text-[13px] font-bold">{e.title}</span>
      </Link>
    ))}
  </div>
);

export const PlacesMap = ({ places, events = [], focusId, onSelect, userPos, category }) => {
  const points = useMemo(() => spread(places.filter((p) => typeof p.lat === "number" && typeof p.lng === "number")), [places]);
  const eventPoints = useMemo(() => events.filter((e) => typeof e.lat === "number" && typeof e.lng === "number"), [events]);
  const allPoints = useMemo(() => [...points, ...eventPoints], [points, eventPoints]);
  const focus = points.find((p) => p.id === focusId) || null;
  return (
    <div className="relative h-[60vh] min-h-[360px] max-h-[640px] border border-white/10 isolate" data-testid="places-map">
      <MapContainer center={[47, 5]} zoom={5} scrollWheelZoom={false} className="h-full w-full bg-[#0a0a0a]">
        <TileLayer
          className="lgtdz-dark-tiles"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds points={allPoints} focus={focus} userPos={userPos} />
        {userPos && (
          <Marker position={[userPos.lat, userPos.lng]} icon={userIcon} zIndexOffset={1000}>
            <Popup><div className="lgtdz-popup" data-testid="places-map-user-popup"><p className="lgtdz-popup-title">Vous êtes ici</p></div></Popup>
          </Marker>
        )}
        {points.map((p) => (
          <Marker key={p.id} position={[p.lat, p.lng]} icon={icon(p.categories?.includes(category) ? category : p.category, p.id === focusId)}
            eventHandlers={{ click: () => onSelect?.(p.id) }}>
            <Popup>
              <div className="lgtdz-popup" data-testid={`places-map-popup-${p.id}`}>
                {p.image && <img src={p.image} alt={p.name} className="lgtdz-popup-photo" data-testid={`places-map-photo-${p.id}`} />}
                <p className="lgtdz-popup-kicker">{(p.categories?.length ? p.categories : [p.category]).map((c) => CATEGORY_STYLE[c]?.label).join(" · ")}</p>
                <p className="lgtdz-popup-title">{p.name}</p>
                <p className="lgtdz-popup-meta">{p.address ? `${p.address}, ` : ""}{p.city} · {p.country}</p>
                {!p.geo_precise && <p className="lgtdz-popup-note">Position approximative (ville)</p>}
                {isOpenNow(p.hours) !== null && (
                  <p className="lgtdz-popup-hours" data-testid={`places-map-open-${p.id}`}>
                    <span className={isOpenNow(p.hours) ? "is-open" : "is-closed"}>{isOpenNow(p.hours) ? "Ouvert maintenant" : "Fermé"}</span> Aujourd'hui : {formatSlots(p.hours[todayKey()])}
                  </p>
                )}
                {p.upcoming?.length > 0 && <PlaceConcerts events={p.upcoming} />}
                <GoButton place={p} testId={`places-map-go-${p.id}`} className="mt-3" />
              </div>
            </Popup>
          </Marker>
        ))}
        {eventPoints.map((e) => (
          <Marker key={`ev-${e.id}`} position={[e.lat, e.lng]} icon={eventIcon} zIndexOffset={500}>
            <Popup>
              <div className="lgtdz-popup" data-testid={`events-map-popup-${e.id}`}>
                <p className="lgtdz-popup-kicker">Concert · {formatEventDate(e.date)}</p>
                <p className="lgtdz-popup-title">{e.title}</p>
                <p className="lgtdz-popup-meta">{[e.venue, e.city].filter(Boolean).join(" · ")}</p>
                <div className="flex gap-4 mt-3">
                  <Link to={`/agenda/${e.slug}`} className="text-[11px] uppercase tracking-[0.2em] font-bold text-white underline decoration-primary decoration-2 underline-offset-4" data-testid={`events-map-link-${e.id}`}>Voir le concert</Link>
                  <GoButton place={e} testId={`events-map-go-${e.id}`} />
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};
